import React, { useState } from "react";
import { motion } from "framer-motion";
import logo from "../images/isuzulogowhite.png";
import google from "../images/google.png";
import { supabase } from "../shared/supabase";

export default function Home() {
    const [formData, setFormData] = useState({
        customer_name: "",
        contact: "",
        email: "",
        vehicle_reg: "",
        q1_first_time_fix: "",
        q2_advisor_friendliness: "",
        q3_appointment_ease: "",
        q4_parts_arrangement: "",
        q5_on_time_delivery: "",
        q6_facility_rating: "",
        general_feedback: "",
    });
    const [error, setError] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const yesNoOptions = ["Yes", "No"];
    const ratingOptions = ["Awesome", "Excellent", "Good", "Fair", "Poor"];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");

        // Required fields including vehicle registration
        const requiredFields = [
            "customer_name",
            "contact",
            "vehicle_reg",
            "q1_first_time_fix",
            "q2_advisor_friendliness",
            "q3_appointment_ease",
            "q4_parts_arrangement",
            "q5_on_time_delivery",
            "q6_facility_rating",
        ];

        // Check for missing required fields
        const missingField = requiredFields.find((field) => !formData[field]);
        if (missingField) {
            setError("Please fill all required fields.");
            setIsLoading(false);
            return;
        }

        // Validate email (basic regex)
        if (formData.email) {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(formData.email)) {
                setError("Please enter a valid email address.");
                setIsLoading(false);
                return;
            }
        }

        // Validate Pakistani phone number
        const phoneRegex = /^03\d{9}$/; // Starts with 03, followed by exactly 9 digits
        if (!phoneRegex.test(formData.contact)) {
            setError("Please enter a valid Pakistani mobile number (03XXXXXXXXX).");
            setIsLoading(false);
            return;
        }

        try {
            const { error } = await supabase
                .from("isuzu_feedback")
                .insert([formData]);

            if (error) {
                console.error("Supabase Error:", error);
                setError("Submission failed: " + error.message);
            } else {
                // Reset form
                setFormData({
                    customer_name: "",
                    contact: "",
                    email: "",
                    vehicle_reg: "",
                    q1_first_time_fix: "",
                    q2_advisor_friendliness: "",
                    q3_appointment_ease: "",
                    q4_parts_arrangement: "",
                    q5_on_time_delivery: "",
                    q6_facility_rating: "",
                    general_feedback: "",
                });
                setShowPopup(true);
                // Hide popup after 4 seconds
                setTimeout(() => setShowPopup(false), 4000);
            }
        } catch (err) {
            console.error("Unexpected Error:", err);
            setError("Something went wrong. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };


    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
    };
    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 },
    };

    return (
        <div className="min-h-screen font-[Poppins] bg-gradient-to-br from-red-50 via-white to-gray-100 relative overflow-x-hidden">
            {/* HEADER */}
            <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80 border-b border-red-200 shadow-md py-3">
                <div className="container mx-auto flex justify-center">
                    <img src={logo} alt="Isuzu Ravi Motors" className="h-20 w-auto" />
                </div>
            </header>

            {/* MAIN CONTENT */}
            <main className="pt-32 pb-20 px-4 max-w-7xl mx-auto grid lg:grid-cols-2 gap-10">
                {/* LEFT: Feedback Form */}
                <motion.div variants={containerVariants} initial="hidden" animate="visible">
                    <div className="relative bg-white/80 backdrop-blur-lg rounded-3xl border border-red-200 shadow-lg p-10">
                        <h2 className="text-4xl font-bold text-red-800 mb-4">Share Your Feedback</h2>
                        <p className="text-gray-700 mb-6">Help us improve your Isuzu experience!</p>
                        {error && <div className="text-white bg-red-600 px-4 py-3 rounded mb-4">{error}</div>}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {["customer_name", "contact", "email", "vehicle_reg"].map((field, i) => (
                                <motion.div key={field} variants={itemVariants}>
                                    <label className="text-red-700 font-semibold">
                                        {field === "customer_name" ? "Name *" :
                                            field === "contact" ? "Contact *" :
                                                field === "email" ? "Email (optional)" : "Vehicle Reg *"}
                                    </label>
                                    <input
                                        type="text"
                                        name={field}
                                        value={formData[field]}
                                        onChange={handleChange}
                                        className="w-full mt-2 px-4 py-3 rounded-xl border border-gray-300 focus:border-red-600 focus:ring-2 focus:ring-red-300 outline-none"
                                        placeholder="Enter here..."
                                        required={field === "customer_name" || field === "contact"}
                                    />
                                </motion.div>
                            ))}

                            {/* Questions */}
                            {[
                                { key: "q1_first_time_fix", label: "Was your vehicle fixed right the 1st time? *", options: yesNoOptions },
                                { key: "q2_advisor_friendliness", label: "Friendliness of service advisor *", options: ratingOptions },
                                { key: "q3_appointment_ease", label: "Ease of appointment *", options: ratingOptions },
                                { key: "q4_parts_arrangement", label: "Arranging replacement parts *", options: ratingOptions },
                                { key: "q5_on_time_delivery", label: "On-time delivery? *", options: yesNoOptions },
                                { key: "q6_facility_rating", label: "Workshop facility rating *", options: ratingOptions },
                            ].map((q) => (
                                <motion.div key={q.key} variants={itemVariants}>
                                    <label className="text-red-700 font-semibold">{q.label}</label>
                                    <div className="flex flex-wrap gap-4 mt-2">
                                        {q.options.map((opt) => (
                                            <label key={opt} className={`cursor-pointer px-4 py-2 rounded-full border ${formData[q.key] === opt ? "bg-red-600 text-white border-red-600" : "bg-gray-100 text-gray-800 border-gray-300"
                                                }`}>
                                                <input
                                                    type="radio"
                                                    name={q.key}
                                                    value={opt}
                                                    checked={formData[q.key] === opt}
                                                    onChange={handleChange}
                                                    className="sr-only"
                                                    required
                                                />
                                                {opt}
                                            </label>
                                        ))}
                                    </div>
                                </motion.div>
                            ))}

                            <motion.div variants={itemVariants}>
                                <label className="text-red-700 font-semibold">General Feedback</label>
                                <textarea
                                    name="general_feedback"
                                    value={formData.general_feedback}
                                    onChange={handleChange}
                                    rows={4}
                                    className="w-full mt-2 px-4 py-3 rounded-xl border border-gray-300 focus:border-red-600 focus:ring-2 focus:ring-red-300 outline-none"
                                    placeholder="Any additional feedback..."
                                />
                            </motion.div>

                            <motion.button
                                type="submit"
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                disabled={isLoading}
                                className="w-full py-4 bg-gradient-to-r from-red-600 to-red-800 text-white font-bold rounded-xl shadow-lg"
                            >
                                {isLoading ? "Submitting..." : "Submit Feedback"}
                            </motion.button>
                        </form>
                    </div>
                </motion.div>

                {/* RIGHT: Cards */}
                <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
                    {/* Google Review */}
                    <motion.div variants={itemVariants} className="bg-white/90 backdrop-blur-md rounded-2xl border border-red-200 p-8 shadow-lg text-center">
                        <img src={google} alt="Google" className="w-20 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-red-800 mb-2">Leave a Google Review</h3>
                        <p className="text-gray-700 mb-4">Help others discover Isuzu Ravi Motors by leaving a review.</p>
                        <button
                            onClick={() => window.open("https://www.google.com/search?sca_esv=7ad43583249a6c5a&rlz=1C1JJTC_enPK1113PK1113&sxsrf=AE3TifML0R06yPH21k0Fnk_FCQIAY6m9mg:1763721680113&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-E7innOPxORBIl4kxDCBBk8W_jtjxwXBVqkz5ky_NsQwPqVQ8eN-o0Ezji1VF6hJBbRSKhLwbcgNBgzeFHmHFfLIbSnfjKqMak8O9HioPdVzvt4fmAQ%3D%3D&q=ISUZU+RAVI+MOTORS+Reviews&sa=X&ved=2ahUKEwiP_eahh4ORAxUR0QIHHS0FK0gQ0bkNegQIIRAE&biw=1280&bih=585&dpr=1.25#lrd=0x39190219c2df4f3f:0x46426f7313ada28c,3,,,,", "_blank")}
                            className="px-6 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition"
                        >
                            Write Review
                        </button>
                    </motion.div>

                    {/* Why Feedback Matters */}
                    <motion.div variants={itemVariants} className="bg-red-50/80 backdrop-blur-md rounded-2xl border border-red-200 p-8 shadow-lg">
                        <h3 className="text-2xl font-bold text-red-800 mb-4">Why Feedback Matters</h3>
                        <ul className="space-y-2 list-disc list-inside text-gray-700">
                            <li>Helps improve services</li>
                            <li>Guides future customers</li>
                            <li>Motivates and recognizes our team</li>
                        </ul>
                    </motion.div>

                    {/* Need Direct Support */}
                    <motion.div
                        variants={itemVariants}
                        className="bg-white/90 backdrop-blur-md rounded-2xl border border-red-200 p-8 shadow-lg"
                    >
                        <h3 className="text-2xl font-bold text-red-800 mb-4">Need Direct Support?</h3>

                        {/* Contact Section */}
                        

                        <p className="text-gray-700 font-semibold mb-1">📍 Postal Address</p>
                        <p className="text-gray-700 mb-4">
                            ISUZU RAVI MOTORS<br />
                            12 KM, Multan Road, Niaz Baig, Lahore, Pakistan
                        </p>
                        <hr className="my-6 border-red-200" />

                        <p className="text-gray-700 font-semibold mb-1">📞 Phone & Support</p>
                        <p className="text-gray-700">
                            Inquiry: 0310-3724444<br />
                            Sales: +92 (42)-35299549, +92-311-7772352<br />
                            Service: +92 (42)-35299585, +92-333-3332420<br />
                            Parts: +92-334-1043353
                        </p>

                        <hr className="my-6 border-red-200" />

                        <p className="text-gray-700 font-semibold mb-1">✉️ Emails</p>
                        <p className="text-gray-700">
                            cr@isuzuravi.com<br />
                            sales@isuzuravi.com<br />
                            service@isuzuravi.com<br />
                            parts@isuzuravi.com
                        </p>
                    </motion.div>

                </motion.div>
            </main>

            {/* Success Popup */}
            {showPopup && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                >
                    <div className="bg-white rounded-3xl p-10 shadow-2xl text-center max-w-md mx-auto">
                        <h2 className="text-3xl font-bold text-red-800 mb-2">Thank You!</h2>
                        <p className="text-gray-700">Your feedback has been successfully submitted.</p>
                    </div>
                </motion.div>
            )}
        </div>
    );
}
